import type { ReactNode } from 'react';

import { useSession } from '../../composants/session.tsx';
import { CANAUX, LIBELLE_CANAL, type Canal } from './types.ts';

/**
 * Où ce produit se vend, et à quel prix — RG-196, RG-032.
 *
 * Deux cases commandent tout, et elles sont cochées par défaut : le cas
 * courant est « je vends la même chose au même prix partout », et il ne doit
 * demander aucun geste.
 *
 * Décochées, elles ouvrent le détail boutique par boutique. Les CANAUX y sont
 * propres à chaque boutique : le même riz au détail à Cotonou et exclusivement
 * en gros à Porto-Novo.
 */

export interface EtatDistribution {
  /** Toutes les boutiques actives, sur tous les canaux déclarés. */
  partout: boolean;
  /** Un seul prix pour toutes les boutiques (RG-032). */
  prixCommun: boolean;
  /** Par boutique : ses canaux, et ses prix si le prix n'est pas commun. */
  parBoutique: Record<string, { canaux: Canal[]; prix: Record<string, string> }>;
}

export const DISTRIBUTION_INITIALE: EtatDistribution = {
  partout: true,
  prixCommun: true,
  parBoutique: {},
};

export function Distribution({
  canauxDuProduit,
  valeur,
  onChange,
}: {
  /** Canaux déclarés du produit : on ne distribue que parmi eux. */
  canauxDuProduit: Canal[];
  valeur: EtatDistribution;
  onChange: (etat: EtatDistribution) => void;
}): ReactNode {
  const { boutiques } = useSession();

  // Une seule boutique : il n'y a rien à répartir, le bloc n'apparaît pas.
  if (boutiques.length < 2) {
    return null;
  }

  const detail = !valeur.partout || !valeur.prixCommun;

  function canauxDe(boutiqueId: string): Canal[] {
    return valeur.parBoutique[boutiqueId]?.canaux ?? canauxDuProduit;
  }

  function basculerCanal(boutiqueId: string, canal: Canal): void {
    const actuels = canauxDe(boutiqueId);
    const suivants = actuels.includes(canal)
      ? actuels.filter((element) => element !== canal)
      : [...actuels, canal];

    onChange({
      ...valeur,
      parBoutique: {
        ...valeur.parBoutique,
        [boutiqueId]: {
          canaux: suivants,
          prix: valeur.parBoutique[boutiqueId]?.prix ?? {},
        },
      },
    });
  }

  function definirPrix(boutiqueId: string, canal: Canal, montant: string): void {
    onChange({
      ...valeur,
      parBoutique: {
        ...valeur.parBoutique,
        [boutiqueId]: {
          canaux: canauxDe(boutiqueId),
          prix: { ...(valeur.parBoutique[boutiqueId]?.prix ?? {}), [canal]: montant },
        },
      },
    });
  }

  const tableau = (
    <div className="tableau-defilant" style={{ marginTop: 12 }}>
      <table>
        <thead>
          <tr>
            <th>Boutique</th>
            {canauxDuProduit.map((canal) => (
              <th key={canal}>{LIBELLE_CANAL[canal]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {boutiques.map((boutique) => (
            <tr key={boutique.id}>
              <td>
                <span className="mono">{boutique.code}</span> {boutique.nom}
              </td>
              {canauxDuProduit.map((canal) => (
                <CelluleCanal
                  key={canal}
                  canal={canal}
                  boutique={boutique}
                  actif={valeur.partout || canauxDe(boutique.id).includes(canal)}
                  choixCanal={!valeur.partout}
                  prix={valeur.prixCommun ? null : (valeur.parBoutique[boutique.id]?.prix[canal] ?? '')}
                  onBasculer={() => {
                    basculerCanal(boutique.id, canal);
                  }}
                  onPrix={(montant) => {
                    definirPrix(boutique.id, canal, montant);
                  }}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <fieldset style={{ border: 0, padding: 0, margin: '14px 0 0' }}>
      <legend className="indicateur__libelle">Où le vendre</legend>

      <label style={{ display: 'block', marginTop: 8, fontWeight: 400 }}>
        <input
          type="checkbox"
          style={{ width: 'auto', marginRight: 8 }}
          checked={valeur.partout}
          onChange={(evenement) => {
            onChange({ ...valeur, partout: evenement.target.checked });
          }}
        />
        Vendre dans toutes les boutiques ({boutiques.length})
      </label>

      {/* Décochée : le tableau boutique par boutique apparaît ICI, juste
          avant « Même prix », puisque c'est cette case qui décide alors
          des canaux — le prix commun n'a plus de sens sans « partout ». */}
      {!valeur.partout && tableau}

      <label style={{ display: 'block', marginTop: 6, fontWeight: 400 }}>
        <input
          type="checkbox"
          style={{ width: 'auto', marginRight: 8 }}
          checked={valeur.prixCommun}
          onChange={(evenement) => {
            onChange({ ...valeur, prixCommun: evenement.target.checked });
          }}
        />
        Même prix dans toutes les boutiques
      </label>

      {/* Cochée : le tableau ne ressort qu'à la décoche de « Même prix »,
          pour donner un prix par boutique — sa place naturelle reste APRÈS
          les deux cases dans ce cas-là. */}
      {valeur.partout && detail && tableau}

      {!valeur.partout && (
        <div className="entete__sous" style={{ marginTop: 8 }}>
          Une boutique sans aucun canal coché ne vendra pas ce produit.
        </div>
      )}
    </fieldset>
  );
}

/** Une case de canal, et son prix quand le prix n'est pas commun. */
function CelluleCanal({
  canal,
  boutique,
  actif,
  choixCanal,
  prix,
  onBasculer,
  onPrix,
}: {
  canal: Canal;
  boutique: { id: string; code: string; nom: string };
  actif: boolean;
  choixCanal: boolean;
  /** `null` quand le prix est commun : la colonne ne montre que la case. */
  prix: string | null;
  onBasculer: () => void;
  onPrix: (montant: string) => void;
}): ReactNode {
  const intitule = `${LIBELLE_CANAL[canal]} — ${boutique.code}`;

  return (
    <td>
      {choixCanal && (
        <label style={{ fontWeight: 400, marginBottom: prix === null ? 0 : 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 6 }}
            checked={actif}
            aria-label={intitule}
            onChange={onBasculer}
          />
          {prix === null ? intitule : 'Vendu'}
        </label>
      )}

      {prix !== null && (
        <input
          type="number"
          min={1}
          step={1}
          value={prix}
          disabled={!actif}
          placeholder="prix"
          aria-label={`Prix ${intitule}`}
          onChange={(evenement) => {
            onPrix(evenement.target.value);
          }}
        />
      )}
    </td>
  );
}

/**
 * Traduit l'état de l'écran en charge utile pour l'API.
 *
 * `distribution` reste ABSENTE quand « partout » est coché : c'est le défaut
 * du serveur, et l'envoyer explicitement figerait la liste des boutiques du
 * jour, sans les suivantes.
 */
export function versCharge(
  etat: EtatDistribution,
  canauxDuProduit: Canal[],
  boutiques: { id: string }[],
  prixCommuns: Record<string, string>,
): {
  distribution?: { boutique_id: string; canaux: Canal[] }[];
  prix: { canal: Canal; montant: number; boutique_id?: string }[];
} {
  const canauxDe = (boutiqueId: string): Canal[] =>
    etat.parBoutique[boutiqueId]?.canaux ?? canauxDuProduit;

  const prix: { canal: Canal; montant: number; boutique_id?: string }[] = [];

  if (etat.prixCommun) {
    for (const canal of canauxDuProduit) {
      const montant = Number(prixCommuns[canal] ?? '');
      if (Number.isFinite(montant) && montant > 0) {
        prix.push({ canal, montant });
      }
    }
  } else {
    for (const boutique of boutiques) {
      for (const canal of canauxDe(boutique.id)) {
        if (!etat.partout && !canauxDe(boutique.id).includes(canal)) {
          continue;
        }
        const montant = Number(etat.parBoutique[boutique.id]?.prix[canal] ?? '');
        if (Number.isFinite(montant) && montant > 0) {
          prix.push({ canal, montant, boutique_id: boutique.id });
        }
      }
    }
  }

  return {
    ...(etat.partout
      ? {}
      : {
          distribution: boutiques.map((boutique) => ({
            boutique_id: boutique.id,
            canaux: canauxDe(boutique.id).filter((canal) =>
              canauxDuProduit.includes(canal),
            ),
          })),
        }),
    prix,
  };
}

export { CANAUX };
